import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ProjectRegeneration } from 'redux/middleware/api/creation-service/types';
import { getEmbedConfiguration } from 'redux/middleware/api/embed-configuration-service/actions';
import { getProjectTemplateCompatibilityFromConfigurationId } from 'redux/middleware/api/headliner-user-service';
import { fetchVideoExportStatus } from 'redux/middleware/api/video-export-service/actions';
import { loadVideosForConfigs } from 'redux/modules/common/actions';
import { getLatestRegeneration } from 'redux/modules/project/actions';
import { clearCreateCreationAsset } from 'redux/modules/wizard-export/actions';
import { Dispatch } from 'redux/types';
import { IEmbedConfig } from 'types';

export interface UseGetProjectConfigProps {
  projectId: string;
  widgetId: string;
  embedConfigurationUuid?: string;
}

export interface UseGetProjectConfigResult {
  widgetId: string;
  embedConfig: IEmbedConfig | undefined;
  projectLatestRegeneration: ProjectRegeneration | undefined;
  isUCSCompatible: boolean;
  fetchProjectConfig: () => Promise<void>;
  reset: () => void;
}

/**
 * Custom hook to fetch and store the configuration for a specific project.
 */
export default function useGetProjectConfig({
  projectId,
  widgetId,
  embedConfigurationUuid,
}: UseGetProjectConfigProps): UseGetProjectConfigResult {
  const [embedConfig, setEmbedConfig] = useState<IEmbedConfig | undefined>();
  const [isUCSCompatible, setIsUCSCompatible] = useState<boolean | undefined>();
  const [projectLatestRegeneration, setProjectLatestRegeneration] = useState<
    ProjectRegeneration | undefined
  >();
  const isLoadingConfigRef = useRef(false);

  const dispatch = useDispatch<Dispatch>();

  const fetchProjectConfig = useCallback(async (): Promise<void> => {
    if (!projectId || isLoadingConfigRef?.current) {
      return;
    }

    isLoadingConfigRef.current = true;

    try {
      const projectLatestRegenerationResponse = await dispatch(
        getLatestRegeneration(projectId),
      );

      const wid =
        projectLatestRegenerationResponse?.widgetId ??
        widgetId ??
        embedConfigurationUuid;

      if (!wid) {
        return;
      }

      const [embedConfigResponse, compatibilityResponse] = await Promise.all([
        dispatch(getEmbedConfiguration(wid)),
        dispatch(getProjectTemplateCompatibilityFromConfigurationId(wid)),
        dispatch(fetchVideoExportStatus(wid)),
      ]);

      const fetchedEmbedConfig =
        embedConfigResponse?.response?.configuration?.embedConfig;

      await dispatch(
        loadVideosForConfigs(fetchedEmbedConfig, { includeVariations: true }),
      );

      setEmbedConfig(fetchedEmbedConfig);
      setIsUCSCompatible(
        compatibilityResponse?.response?.entities?.widgetTemplateCompatibility[
          wid
        ]?.ucsEditor?.isCompatible,
      );
      setProjectLatestRegeneration(projectLatestRegenerationResponse);
    } catch (error) {
      throw new Error('Error getting project config');
    } finally {
      isLoadingConfigRef.current = false;
    }
  }, [dispatch, embedConfigurationUuid, projectId, widgetId]);

  const reset = useCallback(() => {
    setEmbedConfig(undefined);
    setIsUCSCompatible(false);
    setProjectLatestRegeneration(undefined);
    dispatch(clearCreateCreationAsset());
    isLoadingConfigRef.current = false;
  }, [dispatch]);

  return {
    widgetId:
      projectLatestRegeneration?.widgetId ?? widgetId ?? embedConfigurationUuid,
    embedConfig,
    projectLatestRegeneration,
    isUCSCompatible,
    fetchProjectConfig,
    reset,
  };
}
